import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { ApiError, isApiError, type FieldError } from '@/api/errors'

/** Translated message for an error code; falls back to the server `message`, then a generic text. */
export function errorText(t: TFunction, exists: (key: string) => boolean, error: unknown): string {
  if (isApiError(error)) {
    const code = error.code === 'too_many_requests' && !error.retryAfter ? 'too_many_requests_nowait' : error.code
    const key = `errors.codes.${code}`
    if (exists(key)) return t(key, error.retryAfter ? { seconds: error.retryAfter } : {})
    if (error.message) return error.message
  }
  return t('errors.generic')
}

export function fieldErrorText(t: TFunction, exists: (key: string) => boolean, fieldError: FieldError): string {
  const key = `errors.fields.${fieldError.code}`
  if (exists(key)) return t(key, (fieldError.params ?? {}) as Record<string, unknown>)
  return fieldError.message || t('errors.fields.invalid')
}

export function useApiErrorText() {
  const { t, i18n } = useTranslation()
  return useCallback((error: unknown) => errorText(t, (k) => i18n.exists(k), error), [t, i18n])
}

export interface MappedErrors {
  /** field name -> first translated message */
  fields: Record<string, string>
  /** message for errors that are not tied to a field */
  form: string | undefined
}

/** Maps an ApiError to per-field messages plus a form-level message. */
export function useMapApiError() {
  const { t, i18n } = useTranslation()
  return useCallback(
    (error: unknown, fieldAliases: Record<string, string> = {}): MappedErrors => {
      const exists = (k: string) => i18n.exists(k)
      if (!(error instanceof ApiError)) return { fields: {}, form: error ? errorText(t, exists, error) : undefined }
      const fields: Record<string, string> = {}
      for (const [name, list] of Object.entries(error.fieldErrors)) {
        const first = list[0]
        if (first) fields[fieldAliases[name] ?? name] = fieldErrorText(t, exists, first)
      }
      const hasFields = Object.keys(fields).length > 0
      return { fields, form: hasFields && error.code === 'validation_failed' ? undefined : errorText(t, exists, error) }
    },
    [t, i18n],
  )
}
